// frontend/src/widgets/OpportunityAIInsightsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-08 16:27 BST
//
// Description:
//   Right-column bottom third:
//   AI insights panel (local heuristics for now; no backend dependency).
//   Later: replace with /api/opportunities/{id}/insights backed by model + CRM activity.
//
// Lifecycle Status:
//   Active local-heuristic insight widget.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing heuristic insight logic and single render path.
//   - Uses natural card height and visible insight-list overflow on mobile.
//   - Retains the existing desktop fill/internal-scroll behaviour at md and above.
//   - Allows long heuristic text to wrap safely on narrow screens.
//   - Keeps portal tooltips unclipped and constrains them to the mobile viewport.
//   - Adds keyboard focus and touch access to the existing tooltip content so the
//     help text is not dependent on desktop mouse hover.
//
// Notes:
//   - No internal heading: WidgetWrapper provides the title.
//   - Tooltips use the app gold-standard tooltip pattern (portal to document.body,
//     unclipped, theme primary background).
//   - No question-mark icons. No native title tooltips.
//   - Heuristics remain local and do not write back to the database.
//   - No backend/API dependency is introduced by this mobile pass.
//
// Future Intent:
//   - Replace local heuristics with /api/opportunities/{id}/insights backed by
//     model output and CRM activity signals.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import { useTheme } from "../context/ThemeContext.jsx";

// -------------------- Gold-standard Tooltip --------------------
// Portal to document.body, unclipped, label-hover/focus/touch, theme primary bg.
function useGoldTooltip(theme) {
  const [open, setOpen] =
    useState(false);

  const [text, setText] =
    useState("");

  const [pos, setPos] =
    useState({
      x: 0,
      y: 0,
    });

  const rafRef =
    useRef(null);

  const openRef =
    useRef(false);

  const touchHideTimerRef =
    useRef(null);

  const bg =
    theme?.primary_color ||
    "#111827";

  const clearTouchHideTimer =
    useCallback(() => {
      if (
        touchHideTimerRef.current
      ) {
        window.clearTimeout(
          touchHideTimerRef.current
        );

        touchHideTimerRef.current =
          null;
      }
    }, []);

  const hide =
    useCallback(() => {
      clearTouchHideTimer();

      openRef.current =
        false;

      setOpen(false);
      setText("");
    }, [
      clearTouchHideTimer,
    ]);

  const show =
    useCallback(
      (tooltipText) => {
        const value =
          String(
            tooltipText ||
              ""
          ).trim();

        if (!value) {
          return;
        }

        clearTouchHideTimer();

        openRef.current =
          true;

        setText(value);
        setOpen(true);
      },
      [
        clearTouchHideTimer,
      ]
    );

  const move =
    useCallback(
      (
        clientX,
        clientY
      ) => {
        if (
          !openRef.current
        ) {
          return;
        }

        const pad = 12;

        const viewportWidth =
          window.innerWidth ||
          1200;

        const viewportHeight =
          window.innerHeight ||
          800;

        const tooltipWidth =
          Math.min(
            360,
            Math.max(
              220,
              viewportWidth -
                pad * 2
            )
          );

        let x =
          clientX + 14;

        let y =
          clientY + 14;

        const clampX =
          viewportWidth -
          tooltipWidth -
          pad;

        if (
          x > clampX
        ) {
          x =
            Math.max(
              pad,
              clampX
            );
        }

        const approxHeight =
          88;

        const clampY =
          viewportHeight -
          approxHeight -
          pad;

        if (
          y > clampY
        ) {
          y =
            Math.max(
              pad,
              clampY
            );
        }

        if (
          rafRef.current
        ) {
          cancelAnimationFrame(
            rafRef.current
          );
        }

        rafRef.current =
          requestAnimationFrame(
            () =>
              setPos({
                x,
                y,
              })
          );
      },
      []
    );

  const moveToElement =
    useCallback(
      (element) => {
        if (
          !element
        ) {
          return;
        }

        const rect =
          element.getBoundingClientRect();

        move(
          rect.left +
            Math.min(
              rect.width / 2,
              180
            ),
          rect.bottom
        );
      },
      [
        move,
      ]
    );

  const scheduleTouchHide =
    useCallback(() => {
      clearTouchHideTimer();

      touchHideTimerRef.current =
        window.setTimeout(
          () => {
            openRef.current =
              false;

            setOpen(false);
            setText("");

            touchHideTimerRef.current =
              null;
          },
          2600
        );
      },
      [
        clearTouchHideTimer,
      ]
    );

  useEffect(() => {
    const onScroll = () =>
      hide();

    const onKey = (
      event
    ) => {
      if (
        event.key ===
        "Escape"
      ) {
        hide();
      }
    };

    const onBlur = () =>
      hide();

    window.addEventListener(
      "scroll",
      onScroll,
      true
    );

    window.addEventListener(
      "keydown",
      onKey
    );

    window.addEventListener(
      "blur",
      onBlur
    );

    return () => {
      window.removeEventListener(
        "scroll",
        onScroll,
        true
      );

      window.removeEventListener(
        "keydown",
        onKey
      );

      window.removeEventListener(
        "blur",
        onBlur
      );

      clearTouchHideTimer();

      if (
        rafRef.current
      ) {
        cancelAnimationFrame(
          rafRef.current
        );
      }
    };
  }, [
    clearTouchHideTimer,
    hide,
  ]);

  const Tooltip =
    useMemo(() => {
      if (
        !open ||
        !text
      ) {
        return null;
      }

      const style = {
        position:
          "fixed",
        left:
          pos.x,
        top:
          pos.y,
        zIndex:
          99999,
        width:
          "max-content",
        maxWidth:
          "calc(100vw - 24px)",
        background:
          bg,
        color:
          "#fff",
        padding:
          "10px 12px",
        borderRadius:
          10,
        boxShadow:
          "0 10px 30px rgba(0,0,0,0.22)",
        fontSize:
          12,
        fontWeight:
          800,
        lineHeight:
          1.25,
        whiteSpace:
          "pre-line",
        overflowWrap:
          "anywhere",
        pointerEvents:
          "none",
      };

      return createPortal(
        <div
          role="tooltip"
          style={
            style
          }
        >
          {text}
        </div>,
        document.body
      );
    }, [
      bg,
      open,
      pos.x,
      pos.y,
      text,
    ]);

  const bind =
    useCallback(
      (
        tooltipText
      ) => ({
        tabIndex: 0,

        onMouseEnter: (
          event
        ) => {
          show(
            tooltipText
          );

          move(
            event.clientX,
            event.clientY
          );
        },

        onMouseMove: (
          event
        ) =>
          move(
            event.clientX,
            event.clientY
          ),

        onMouseLeave: () =>
          hide(),

        onFocus: (
          event
        ) => {
          show(
            tooltipText
          );

          requestAnimationFrame(
            () =>
              moveToElement(
                event.currentTarget
              )
          );
        },

        onBlur: () =>
          hide(),

        onTouchStart: (
          event
        ) => {
          const touch =
            event.touches?.[0];

          show(
            tooltipText
          );

          if (touch) {
            requestAnimationFrame(
              () =>
                move(
                  touch.clientX,
                  touch.clientY
                )
            );
          } else {
            requestAnimationFrame(
              () =>
                moveToElement(
                  event.currentTarget
                )
            );
          }

          scheduleTouchHide();
        },
      }),
      [
        hide,
        move,
        moveToElement,
        scheduleTouchHide,
        show,
      ]
    );

  return {
    Tooltip,
    bind,
  };
}

const parseDate = (
  value
) => {
  if (!value) {
    return null;
  }

  const date =
    new Date(
      value
    );

  return Number.isFinite(
    date.getTime()
  )
    ? date
    : null;
};

const daysBetween = (
  start,
  end
) =>
  Math.round(
    (
      end.getTime() -
      start.getTime()
    ) /
      (
        1000 *
        60 *
        60 *
        24
      )
  );

export default function OpportunityAIInsightsWidget({
  opportunity,
}) {
  const { theme } =
    useTheme();

  const {
    Tooltip,
    bind,
  } =
    useGoldTooltip(
      theme
    );

  const insights =
    useMemo(() => {
      if (
        !opportunity
      ) {
        return [];
      }

      const stage =
        String(
          opportunity.stage ||
            "—"
        );

      const status =
        String(
          opportunity.status ||
            "—"
        );

      const probability =
        typeof opportunity.probability ===
        "number"
          ? opportunity.probability
          : null;

      const created =
        parseDate(
          opportunity.created_at
        );

      const updated =
        parseDate(
          opportunity.updated_at
        );

      const now =
        new Date();

      const ageDays =
        created
          ? daysBetween(
              created,
              now
            )
          : null;

      const staleDays =
        updated
          ? daysBetween(
              updated,
              now
            )
          : null;

      const out = [];

      // Probability / status signals
      if (
        status.toLowerCase() ===
        "open"
      ) {
        if (
          probability ===
          null
        ) {
          out.push(
            "Set a probability. The UI should force a value (or derive from stage) to keep pipeline useful."
          );
        }

        if (
          probability !==
            null &&
          probability >=
            75
        ) {
          out.push(
            "High probability open deal: ensure next action is scheduled and owner has visibility."
          );
        }

        if (
          probability !==
            null &&
          probability <=
            25
        ) {
          out.push(
            "Low probability: confirm qualification criteria or downgrade stage to avoid pipeline noise."
          );
        }
      }

      // Staleness
      if (
        staleDays !==
          null &&
        staleDays >=
          14
      ) {
        out.push(
          `Stale: no update in ${staleDays} days. Chase customer or log activity.`
        );
      }

      if (
        ageDays !==
          null &&
        ageDays >=
          45 &&
        status.toLowerCase() ===
          "open"
      ) {
        out.push(
          `Ageing: open for ${ageDays} days. Consider close-plan or stage correction.`
        );
      }

      // Stage hints
      if (
        stage.toLowerCase() ===
        "qualification"
      ) {
        out.push(
          "Qualification: confirm budget, authority, need, timeline (BANT-style)."
        );
      }

      if (
        stage.toLowerCase() ===
        "proposal"
      ) {
        out.push(
          "Proposal: attach quote/version, confirm stakeholders, and set expected close date."
        );
      }

      if (
        stage.toLowerCase() ===
        "negotiation"
      ) {
        out.push(
          "Negotiation: record objections, discount logic, and decision date."
        );
      }

      if (
        stage.toLowerCase() ===
        "closed"
      ) {
        out.push(
          "Closed stage: ensure status is won/lost and post-close tasks are created."
        );
      }

      // Basic data completeness
      if (
        !opportunity.expected_close_date
      ) {
        out.push(
          "Missing expected close date. Add it to improve forecasting."
        );
      }

      if (
        !opportunity.primary_contact_id
      ) {
        out.push(
          "Missing primary contact. Assign one so comms don’t float."
        );
      }

      // Keep it short and high-signal.
      return out.slice(
        0,
        7
      );
    }, [
      opportunity,
    ]);

  const card =
    useMemo(
      () => ({
        borderRadius:
          14,
        padding:
          14,
        background:
          theme?.card_bg ||
          "#ffffff",
        border:
          `1px solid ${
            theme?.border_color ||
            "#e5e7eb"
          }`,
        boxShadow:
          "0 2px 10px rgba(0,0,0,0.06)",
        boxSizing:
          "border-box",
        fontFamily:
          theme?.font_primary ||
          "inherit",
        display:
          "flex",
        flexDirection:
          "column",
        gap:
          10,
        minHeight:
          0,
      }),
      [
        theme,
      ]
    );

  const subtle =
    useMemo(
      () => ({
        color:
          theme?.text_muted ||
          "#6b7280",
        fontSize:
          12,
        lineHeight:
          "16px",
        fontWeight:
          800,
        overflowWrap:
          "anywhere",
      }),
      [
        theme,
      ]
    );

  const list =
    useMemo(
      () => ({
        margin:
          0,
        paddingLeft:
          18,
        minHeight:
          0,
      }),
      []
    );

  const item =
    useMemo(
      () => ({
        fontSize:
          12,
        lineHeight:
          "18px",
        fontWeight:
          800,
        color:
          theme?.primary_color ||
          "#111827",
        marginBottom:
          8,
        overflowWrap:
          "anywhere",
      }),
      [
        theme,
      ]
    );

  const metaTip =
    "Heuristic insights (local).\nThis is a design stub for now.\nLater: model-backed insights + CRM activity signals.";

  const insightTip =
    "These are quick prompts to reduce pipeline noise.\nThey do not write back to the database (yet).";

  return (
    <div
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={
        card
      }
    >
      {/* No internal heading; wrapper supplies title */}
      <div
        style={
          subtle
        }
        {...bind(
          metaTip
        )}
      >
        {opportunity
          ? "Heuristic insights (local)."
          : "Select an opportunity to see insights."}
      </div>

      {opportunity ? (
        insights.length ===
        0 ? (
          <div
            style={
              subtle
            }
          >
            No insights.
          </div>
        ) : (
          <ul
            className="min-h-0 flex-1 overflow-visible pr-1 md:overflow-auto"
            style={
              list
            }
          >
            {insights.map(
              (
                insight,
                index
              ) => (
                <li
                  key={`${index}-${insight}`}
                  style={
                    item
                  }
                  {...bind(
                    insightTip
                  )}
                >
                  {insight}
                </li>
              )
            )}
          </ul>
        )
      ) : null}

      {Tooltip}
    </div>
  );
}
